import React, { useState, useEffect, useRef } from "react";
import {
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Download,
  CircleCheck,
  CircleCheckBig,
} from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { usePayout } from "../../customHooks/Payout/usePayout";
import AppLoader from "../../components/AppLoader/appLoader";
import Pagination from "../../components/Pagination/Pagination";
import { formatDateTime } from "../../components/Constants/Constants";

const { Option } = Select;

const searchableFields = [
  { value: "order_id", label: "Order ID" },
  { value: "buyer_app_id", label: "Buyer App ID" },
];

const Payout = () => {
  const {
    paginatedPayouts, // Updated name to match your useMemo
    loading,
    totalOrderCount,
    filters,
    setFilters,
    metrics,
    ledger,
    balanceDetails,
    totalPages,
    currentPage,
    setCurrentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    itemsPerPageOptions,
    tenantType,
  } = usePayout();

  // Today's date in YYYY-MM-DD format for date input max
  const today = new Date().toISOString().slice(0, 10);

  const exportCsv = () => {
    const rows = [];
    // Header (buyer-wise settlement fields)
    rows.push([
      "Buyer App Name",
      "Gross Order Value",
      "Total Settled Amount",
      "Seller Net Payout",
      "Settled MSN Fee",
      "Settled Buyer Fee",
      "Pending Settlement",
      "Pending Order Count",
      "Settled Order Count",
      "Last Settlement Action",
    ]);
    // Data rows
    (paginatedPayouts || []).forEach((item) => {
      rows.push([
        item.buyer_app_name || item.buyer_app_id || "",
        item.gross_order_value != null ? Number(item.gross_order_value) : "",
        item.total_settled_amount != null
          ? Number(item.total_settled_amount)
          : "",
        item.seller_net_payout != null ? Number(item.seller_net_payout) : "",
        item.settled_msn_fee != null ? Number(item.settled_msn_fee) : "",
        item.settled_buyer_fee != null ? Number(item.settled_buyer_fee) : "",
        item.pending_settlement != null ? Number(item.pending_settlement) : "",
        item.pending_order_count != null
          ? Number(item.pending_order_count)
          : "",
        item.settled_order_count != null
          ? Number(item.settled_order_count)
          : "",
        formatDateTime
          ? formatDateTime(item.last_settlement_action)
          : item.last_settlement_action || "",
      ]);
    });

    const csvContent = rows
      .map((r) =>
        r
          .map((c) => {
            if (c === null || c === undefined) return "";
            const s = String(c);
            // escape quotes
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
    link.href = url;
    link.setAttribute("download", `payouts_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [showBuyerTable, setShowBuyerTable] = useState(true);
  const [showLedgerTable, setShowLedgerTable] = useState(true);
  const [selectedDateCategory, setSelectedDateCategory] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const customNInputRef = useRef(null);

  const initialWindowOption = (() => {
    const v = filters?.settlement_window;
    if (v === "" || v === undefined || v === null) return "all";
    const valStr = String(v);
    if (["0", "1", "2", "3"].includes(valStr)) return valStr;
    return "custom";
  })();
  const [windowOption, setWindowOption] = useState(initialWindowOption);

  useEffect(() => {
    if (windowOption === "custom" && customNInputRef.current) {
      customNInputRef.current.focus();
      customNInputRef.current.select();
    }
  }, [windowOption]);

  useEffect(() => {
    const v = filters?.settlement_window;
    if (v === "" || v === undefined || v === null) {
      setWindowOption("all");
      return;
    }
    const valStr = String(v);
    if (["0", "1", "2", "3"].includes(valStr)) {
      setWindowOption(valStr);
    } else if (windowOption !== "custom") {
      setWindowOption("custom");
    }
  }, [filters?.settlement_window]);

  const handleWindowSelectChange = (e) => {
    const val = e.target.value;
    setWindowOption(val);
    if (val === "all") {
      handleFilterChange("settlement_window", "");
    } else if (["0", "1", "2", "3"].includes(val)) {
      handleFilterChange("settlement_window", val);
    } else if (val === "custom") {
      // Prepare for custom input; keep current N or empty for new input
      const current = filters?.settlement_window;
      if (
        current === "" ||
        current === undefined ||
        current === null ||
        ["0", "1", "2", "3"].includes(String(current)) === false
      ) {
        handleFilterChange("settlement_window", "");
      }
    }
  };

  const handleCustomNChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    handleFilterChange("settlement_window", digitsOnly);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleSelectChange = (value) => {
    setSelectedDateCategory(value);
    const now = dayjs();

    const dateRangeMap = {
      "today": [now.startOf("day"), now.endOf("day")],
      "last 7-days": [now.subtract(7, "day").startOf("day"), now.endOf("day")],
      "last 1-month": [now.subtract(1, "month").startOf("day"), now.endOf("day")],
      "last 3-months": [now.subtract(3, "months").startOf("day"), now.endOf("day")],
      "last 6-months": [now.subtract(6, "months").startOf("day"), now.endOf("day")],
      "last 1-year": [now.subtract(1, "year").startOf("day"), now.endOf("day")],
      "last 3-years": [now.subtract(3, "year").startOf("day"), now.endOf("day")],
      "last 5-years": [now.subtract(5, "year").startOf("day"), now.endOf("day")],
    };

    if (dateRangeMap[value]) {
      const [start, end] = dateRangeMap[value];
      setDateRange([start, end]);
      handleFilterChange("start_date", start.format("YYYY-MM-DD"));
      handleFilterChange("end_date", end.format("YYYY-MM-DD"));
    } else if (value === "all") {
      setDateRange([null, null]);
      handleFilterChange("start_date", "");
      handleFilterChange("end_date", "");
    }
  };

  const onDateRangeChange = (dates) => {
    const startDate = dates?.[0] ? dayjs(dates[0]) : null;
    const endDate = dates?.[1] ? dayjs(dates[1]) : null;

    setDateRange([startDate, endDate]);
    handleFilterChange(
      "start_date",
      startDate ? startDate.format("YYYY-MM-DD") : "",
    );
    handleFilterChange(
      "end_date",
      endDate ? endDate.format("YYYY-MM-DD") : "",
    );
  };

  const customStartDate = dateRange?.[0] ? dayjs(dateRange[0]) : null;
  const customEndDate = dateRange?.[1] ? dayjs(dateRange[1]) : null;

  const disableFutureDates = (current) =>
    current && current.isAfter(dayjs().endOf("day"));

  const disableStartDate = (current) => {
    if (!current) return false;
    if (disableFutureDates(current)) return true;
    return customEndDate ? current.isAfter(customEndDate, "day") : false;
  };

  const disableEndDate = (current) => {
    if (!current) return false;
    if (disableFutureDates(current)) return true;
    return customStartDate ? current.isBefore(customStartDate, "day") : false;
  };

  const handleCustomStartDateChange = (date) => {
    onDateRangeChange([date || null, customEndDate || null]);
  };

  const handleCustomEndDateChange = (date) => {
    onDateRangeChange([customStartDate || null, date || null]);
  };

  const clearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      settlement_window: "",
      order_id: "",
      order_settlement_status: "",
      buyer_app_id: "",
      search_by: "",
      search_value: "",
    });
    setSelectedDateCategory("all");
    setDateRange([null, null]);
    setCurrentPage(1);
  };

  const toNum = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const getMetricByKeys = (keys) => {
    const matchedKey = keys.find((key) =>
      Object.prototype.hasOwnProperty.call(metrics, key),
    );
    return matchedKey ? toNum(metrics[matchedKey]) : 0;
  };

  const grossOrderValue = getMetricByKeys([
    "gross_order_value"
  ]);

  const settlementBreakdown = [
    {
      entity: "Seller App",
      amountEarned: getMetricByKeys([
        "seller_net_earning"
      ]),
      settledRequested: getMetricByKeys([
        "requested_settled_amount"
      ]),
      unsettledRequested: getMetricByKeys([
        "unsettled_requested_settlement_amount"
      ]),
      pending: getMetricByKeys(["pending_seller_net_payout"]),
    },
    ...(tenantType !== "ISN" ? [{
      entity: "MSN",
      amountEarned: getMetricByKeys([
        "net_msn_commission"
      ]),
      settledRequested: getMetricByKeys([
        "settled_msn_commission"
      ]),
      pending: getMetricByKeys(["pending_msn_commission"]),
    }] : []),
    {
      entity: "Buyer App",
      amountEarned: getMetricByKeys([
        "net_buyer_finder_commission"
      ]),
      settledRequested: getMetricByKeys([
        "settled_buyer_commission"
      ]),
      unsettledRequested: 0,
      pending: getMetricByKeys(["pending_buyer_commission"]),
    },
  ];

  const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;
  const formatNumberIndian = (value) => toNum(value).toLocaleString("en-IN");
  const formatPercentOfGov = (amountEarned) => {
    if (!grossOrderValue) return "0.00%";
    return `${((amountEarned / grossOrderValue) * 100).toFixed(2)}%`;
  };

  return (
    <div className="m-4">
      <h4 className="fw-bold">Payout Management</h4>

      {/* --- Filters --- */}
      <div
        className="bg-white rounded mb-4"
        style={{
          padding: "20px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e9ecef",
        }}
      >
        <div className="row g-2 align-items-end">
          <div className="col-lg-3 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              Search ID
            </label>
            <div
              className="input-group"
              style={{ boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.04)" }}
            >
              <select
                className="form-select form-select-sm"
                value={filters.search_by}
                onChange={(e) =>
                  handleFilterChange("search_by", e.target.value)
                }
                style={{
                  height: "38px",
                  borderRadius: "4px 0 0 4px",
                  fontSize: "13px",
                  flex: "0 0 auto",
                  minWidth: "8rem",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  cursor: "pointer",
                }}
              >
                <option value="" style={{ backgroundColor: "white" }}>
                  Field
                </option>
                {searchableFields.map((s) => (
                  <option
                    key={s.value}
                    value={s.value}
                    style={{ backgroundColor: "white" }}
                  >
                    {s.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter value..."
                value={filters.search_value}
                onChange={(e) =>
                  handleFilterChange("search_value", e.target.value)
                }
                disabled={!filters.search_by}
                style={{
                  height: "38px",
                  borderRadius: "0 4px 4px 0",
                  fontSize: "13px",
                  border: "2px solid #dee2e6",
                  flex: "1",
                  marginLeft: "0.7rem",
                  backgroundColor: !filters.search_by ? "#e0dddd" : "white",
                  cursor: !filters.search_by ? "not-allowed" : "text",
                }}
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              Settlement Window
            </label>
            {windowOption === "custom" ? (
              <div className="input-group">
                <span
                  className="input-group-text"
                  style={{ fontSize: "13px", backgroundColor: "#f8f9fa" }}
                >
                  T+
                </span>
                <input
                  ref={customNInputRef}
                  type="number"
                  min="0"
                  step="1"
                  className="form-control form-control-sm"
                  style={{
                    height: "38px",
                    borderRadius: "0 4px 4px 0",
                    fontSize: "13px",
                  }}
                  placeholder="N"
                  value={filters.settlement_window}
                  onChange={handleCustomNChange}
                  onBlur={() => {
                    if (!filters.settlement_window) {
                      setWindowOption("all");
                      handleFilterChange("settlement_window", "");
                    }
                  }}
                />
              </div>
            ) : (
              <select
                className="form-select form-select-sm"
                style={{
                  height: "38px",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
                value={windowOption}
                onChange={handleWindowSelectChange}
              >
                <option value="all">All Windows</option>
                <option value="0">T+0 (Same Day)</option>
                <option value="1">T+1 (Next Day)</option>
                <option value="2">T+2 (2 Days)</option>
                <option value="3">T+3 (3 Days)</option>
                <option value="custom">Custom (T+N)</option>
              </select>
            )}
          </div>

          <div className="col-lg-3 col-md-6 d-flex justify-content-start align-items-end gap-3">
            <div>
              <label
                className="form-label mb-2 fw-500"
                style={{ fontSize: "13px", color: "#495057" }}
              >
                Date Range
              </label>
              <Select
                style={{ width: 150, height: "38px" }}
                onChange={handleSelectChange}
                value={selectedDateCategory}
              >
                <Option value="all">All</Option>
                <Option value="today">Today</Option>
                <Option value="last 7-days">Last 7 Days</Option>
                <Option value="last 1-month">Last 1 Month</Option>
                <Option value="last 3-months">Last 3 Months</Option>
                <Option value="last 6-months">Last 6 Months</Option>
                <Option value="last 1-year">Last 1 Year</Option>
                <Option value="last 3-years">Last 3 Years</Option>
                <Option value="last 5-years">Last 5 Years</Option>
                <Option value="custom">Custom</Option>
              </Select>
            </div>
            {selectedDateCategory === "custom" && (
              <div>
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <DatePicker
                    className="custom-range-picker"
                    format="MM/DD/YYYY"
                    allowClear
                    placeholder="Start Date"
                    value={customStartDate}
                    onChange={handleCustomStartDateChange}
                    disabledDate={disableStartDate}
                    style={{
                      width: "140px",
                      height: "38px",
                    }}
                  />
                  <span style={{ color: "#6b7280", fontSize: "13px" }}>to</span>
                  <DatePicker
                    className="custom-range-picker"
                    format="MM/DD/YYYY"
                    allowClear
                    placeholder="End Date"
                    value={customEndDate}
                    onChange={handleCustomEndDateChange}
                    disabledDate={disableEndDate}
                    style={{
                      width: "140px",
                      height: "38px",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-2 col-md-6 d-flex justify-content-end ">
            <button
              type="button"
              className="clearFilter"
              onClick={clearFilters}
            >
              <RotateCcw
                size={16}
                style={{
                  marginRight: "6px",
                  verticalAlign: "middle",
                }}
              />
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <AppLoader />
      ) : (
        <>
          {/* --- Gross Order Reference + Breakdown --- */}
          <div className="mb-4">
            <div
              className="card border-0 shadow-sm"
              style={{
                borderRadius: "12px",
                background: "#f8f9fb",
                maxWidth: "25%",
              }}
            >
              <div className="card-body py-3 px-4">
                <div className="d-flex align-items-center mb-2">
                  <div
                    className="d-flex align-items-center justify-content-center me-2"
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      backgroundColor: "#fff4d6",
                    }}
                  >
                    <CircleCheckBig size={20} color="#f59e0b" strokeWidth={2.3} />
                  </div>
                  <div style={{ color: "#4b5563", fontSize: "15px", fontWeight: 600 }}>
                    Gross Order Value
                  </div>
                </div>
                <h3 className="mb-0" style={{ color: "#0f172a", fontWeight: 700 }}>
                  {formatCurrency(grossOrderValue)}
                </h3>
              </div>
            </div>

            <div className="card shadow-sm border-0 mt-3">
              <div className="card-header bg-white py-3 d-flex align-items-center">
                <span className="fw-bold"></span>
                 {/* <div className="ms-auto d-flex gap-2">
                <button
                  className="btn btn-sm btn-light border"
                  onClick={exportCsv}
                  disabled={!paginatedPayouts || paginatedPayouts.length === 0}
                >
                  <Download size={14} className="me-1" />
                  Export CSV
                </button>
                <button
                  className="btn btn-sm btn-light border"
                  onClick={() => setShowBuyerTable(!showBuyerTable)}
                >
                  {showBuyerTable ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
              </div> */}
              </div>
              <div className="table-responsive">
                <table className="table table-bordered align-middle text-center mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Platform</th>
                      <th>Amount Earned (₹)</th>
                      <th>% of GOV</th>
                      <th>Amount Paid (₹)</th>
                      <th>Not Paid / Pending (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlementBreakdown.map((row) => (
                      <tr key={row.entity}>
                        <td>{row.entity}</td>
                        <td>
                          {formatCurrency(row.amountEarned)}
                        </td>
                        <td>{formatPercentOfGov(row.amountEarned)}</td>
                        <td>
                          {row.entity === "Seller App" && (
                            <>
                              Settled: {formatCurrency(row.settledRequested)}
                              <br />
                            </>
                          )}
                          {row.entity !== "Seller App" && (
                            <>
                              {formatCurrency(row.settledRequested)}
                              <br />
                            </>
                          )}
                          {row.entity === "Seller App" && (
                            <>
                              Unsettled: {formatCurrency(row.unsettledRequested)}
                              <br />
                            </>
                          )}
                        </td>
                        <td>{formatCurrency(row.pending)}</td> 
                      </tr>
                    ))}
                    <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                      <td>Total</td>
                      <td>
                        {formatCurrency(
                          settlementBreakdown.reduce((sum, row) => sum + toNum(row.amountEarned), 0)
                        )}
                      </td>
                      <td>-</td>
                      <td>
                        {formatCurrency(
                          settlementBreakdown.reduce((sum, row) => {
                            return sum + toNum(row.settledRequested) + toNum(row.unsettledRequested || 0);
                          }, 0)
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          settlementBreakdown.reduce((sum, row) => sum + toNum(row.pending), 0)
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white py-3 d-flex align-items-center">
              <span className="fw-bold">Buyer Wise Settlement</span>
              <div className="ms-auto d-flex gap-2">
                <button
                  className="btn btn-sm btn-light border"
                  onClick={exportCsv}
                  disabled={!paginatedPayouts || paginatedPayouts.length === 0}
                >
                  <Download size={14} className="me-1" />
                  Export CSV
                </button>
                <button
                  className="btn btn-sm btn-light border"
                  onClick={() => setShowBuyerTable(!showBuyerTable)}
                >
                  {showBuyerTable ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
              </div>
            </div>
            {showBuyerTable && (
              <>
                {paginatedPayouts && paginatedPayouts.length > 0 ? (
                  <div className="table-responsive">
                    <table
                      className="table table-bordered table-hover align-middle text-center"
                      style={{ marginBottom: "0" }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th>Buyer App Name</th>
                          <th>Gross Order Value</th>
                          <th>Total Settled Amount</th>
                          <th>Seller Net Payout</th>
                          <th>Settled MSN Fee</th>
                          <th>Settled Buyer Fee</th>
                          <th>Pending Settlement</th>
                          <th>Pending Order Count</th>
                          <th>Settled Order Count</th>
                          <th>Last Settlement Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPayouts.map((item, i) => (
                          <tr key={i} style={{ height: "41px" }}>
                            <td className="small">
                              {item.buyer_app_name || item.buyer_app_id}
                            </td>
                            <td>
                              ₹
                              {Number(
                                item.gross_order_value || 0,
                              ).toLocaleString("en-IN")}
                            </td>
                            <td>
                              ₹
                              {Number(
                                item.total_settled_amount || 0,
                              ).toLocaleString("en-IN")}
                            </td>
                            <td className="fw-bold text-primary">
                              ₹
                              {Number(
                                item.seller_net_payout || 0,
                              ).toLocaleString("en-IN")}
                            </td>
                            <td>
                              ₹
                              {Number(item.settled_msn_fee || 0).toLocaleString("en-IN")}
                            </td>
                            <td>
                              ₹
                              {Number(
                                item.settled_buyer_fee || 0,
                              ).toLocaleString("en-IN")}
                            </td>
                            <td>
                              ₹
                              {Number(
                                item.pending_settlement || 0,
                              ).toLocaleString("en-IN")}
                            </td>
                            <td>{formatNumberIndian(item.pending_order_count)}</td>
                            <td>{formatNumberIndian(item.settled_order_count)}</td>
                            <td>
                              {item.last_settlement_action===null && "N/A"}
                              {item.last_settlement_action!==null && (
                                formatDateTime
                                  ? formatDateTime(item.last_settlement_action)
                                  : item.last_settlement_action
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#9ca3af",
                      fontSize: "14px",
                    }}
                  >
                    No Records Found
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination (Exactly like Reports) */}
          {/* {totalOrderCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              startItem={startItem}
              endItem={endItem}
              totalItems={totalOrderCount}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={goToPage}
              itemsPerPageOptions={itemsPerPageOptions}
            />
          )} */}
        </>
      )}

      {/* --- Account Ledger Table --- */}
      <div className="card shadow-sm border-0 mt-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <span className="mb-0 fw-bold">NOCA Account Movement</span>

            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
              Balance: ₹
              {formatNumberIndian(balanceDetails.current_balance)}
            </span>
          </div>
          <button
            className="btn btn-sm btn-light border"
            onClick={() => setShowLedgerTable(!showLedgerTable)}
          >
            {showLedgerTable ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
        </div>
        {showLedgerTable && (
          <>
            {ledger && ledger.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="bg-light">
                    <tr className="small text-muted">
                      <th className="px-3">DATE</th>
                      <th>CREDIT (+)</th>
                      <th>DEBIT (-)</th>
                      <th>CHARGES</th>
                      <th className="text-end px-3">CLOSING BALANCE</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {ledger.map((l, idx) => (
                      <tr key={idx} className="small" style={{ height: "32px" }}>
                        <td className="px-3 py-2">{l.date}</td>
                        <td className="text-success">
                          {toNum(l.credit) !== 0 ? `₹${formatNumberIndian(l.credit)}` : "-"}
                        </td>
                        <td className="text-danger">
                          {toNum(l.debit) !== 0 ? `₹${formatNumberIndian(l.debit)}` : "-"}
                        </td>
                        <td className="text-muted">
                          {toNum(l.charges) !== 0 ? `₹${formatNumberIndian(l.charges)}` : "-"}
                        </td>
                        <td className="fw-bold text-end px-3">
                          ₹{formatNumberIndian(l.closing_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ledger && ledger.length > 0 && (
                  <div
                    className="bg-light"
                    style={{
                      padding: "16px",
                      marginTop: "-1px",
                      borderTop: "1px solid #dee2e6",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "16px",
                        fontWeight: "600",
                      }}
                    >
                      <div className="text-muted">
                        Total Credits:{" "}
                        <span className="text-success">
                          ₹{formatNumberIndian(balanceDetails.total_credited)}
                        </span>
                      </div>
                      <div className="text-muted">
                        Total Debits:{" "}
                        <span className="text-danger">
                          ₹{formatNumberIndian(balanceDetails.total_debited)}
                        </span>
                      </div>
                      <div className="text-muted">
                        Total Charges:{" "}
                        <span className="text-black fw-bold">
                          ₹{formatNumberIndian(balanceDetails.total_charges)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#9ca3af",
                  fontSize: "14px",
                }}
              >
                No Records Found
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Payout;
